// 与后端 app/schemas.py Pydantic 模型对齐
// 后端 → 前端契约，保持单一来源

export type FeatureKind = 'dimension' | 'gdt' | 'thread' | 'reference';
export type FeatureStatus = 'AI_FOUND' | 'CONFIRMED' | 'MODIFIED' | 'REJECTED';

export interface GDT {
  symbol: string | null;
  zone: number | null;
  zone_is_diameter: boolean;
  datums: Datum[];            // 有序 JSON 数组
  material_condition: string | null;
  requires_datum: boolean;
  // 非对称公差带偏置量（ASME Y14.5-2018 11.3.1.2 的 `UZ`）：
  // 图纸 `⌓ | 0.6 UZ 0.4 | A | B | C` 里的 0.4，**可正可负**。
  // null / undefined = 对称公差带（历史数据全是这种）。
  uz_offset?: number | null;
}

export interface Datum {
  name: string;
  order: number;
  condition: string | null;
}

export interface Feature {
  id: number;
  drawing_id: number;
  feature_id: string;       // 图内编号 F001（与可见 balloon_no 分离）
  kind: FeatureKind;
  type: string;
  characteristic: string;
  nominal: number | null;
  upper_tol: string | null;
  lower_tol: string | null;
  upper_dev: number | null;
  lower_dev: number | null;
  upper_limit: number | null;
  lower_limit: number | null;
  tolerance_text: string | null;
  fit: string | null;
  is_diameter: boolean;
  unit: string;
  display_text: string;
  display: string;
  modifiers: string[];
  qty: number;
  page: number;
  page_no: number;          // v0.21：多页归属（= page）
  x: number;                // 特征锚点 X（引线起止点，PDF 点）
  y: number;
  anchor_x: number;
  anchor_y: number;
  // 气球持久化几何（v0.21）
  center_x: number | null;
  center_y: number | null;
  leader_points: number[][];  // [[x,y], ...] 引线折点
  view: number;
  balloon_no: number;       // 可见编号
  seq: number;
  method: string;
  precision: number;
  // 实测值（Milestone B）
  measured: number | null;
  samples: number[];
  pass_fail: 'Pass' | 'Fail' | null;
  status: FeatureStatus;
  enabled: boolean;
  note: string;
  // Sprint 4.3.2 候选过滤（ACCEPT / REVIEW；历史无值视为 ACCEPT）
  filter_status?: string | null;     // 'ACCEPT' | 'REVIEW'
  filter_reason?: string | null;     // REVIEW 时的判定原因
  filter_region?: string | null;     // 判定区域
  // v0.49：特殊特性（AS9102 / PPAP / SIP「B 列」）。
  // 'CC' 关键特性 / 'SC' 重要特性 / null 普通。取值候选见 lib/annot.ts CC_SC_OPTS。
  cc_sc?: 'CC' | 'SC' | null;
  gdt: GDT | null;
}

export interface DrawingSummary {
  id: number;
  filename: string;
  page_count: number;
  status: string;
  created_at: string | null;
  feature_count: number;
  // 源文件指纹（mtime_ms_size）：页面位图 URL 的版本参数。
  // drawings.id 会被复用（删光图纸后重新上传 → 新图纸拿到 id=1），
  // 若 URL 只用 id，浏览器 max-age 强缓存会把上一张图纸的底图吐出来。
  image_version?: string;
  /** 归属文件夹（v0.32）；null/undefined = 根目录未归类。 */
  folder_id?: number | null;
  /**
   * 归户到的「零件主数据」台账行（v0.38.1）；null/undefined = 未归户。
   *
   * ★ 没有这个字段，两个栏目就只是各存各的：图纸工作台看不出「这张图属于
   *   哪个零件」，零件主数据也列不出「这个零件有哪些图纸」。
   */
  part_master_id?: number | null;
}

/**
 * 图纸列表的**范围**（v0.40，用户 2026-10-10 定顶层结构）。
 *
 * 用户原话：「他的结构应该是这样，BOM（零件主数据）—— 图纸」。
 * ⇒ 图纸不再是独立的顶层栏目，而是**某个零件下面的内容**：
 *
 *   · `all`        —— 全部图纸（原「文件夹树」视角，用于未归类/按上传批次找）
 *   · `part`       —— 某个零件名下已归户的图纸
 *   · `unassigned` —— 还没归户的图纸（零件被删后解除归户的也落这里）
 *
 * ⚠️ 只有**一个**消费方 `DrawingTree`（图纸列表的唯一实现）——
 *    范围只决定它取哪一段数据、以及是否渲染文件夹层，**不许另写第二个列表**。
 */
export type DrawingScope =
  | { kind: 'all' }
  | { kind: 'part'; id: number; label?: string }
  | { kind: 'unassigned' };

export interface DrawingDetail extends DrawingSummary {
  width: number;
  height: number;
  source_type: string;
  features: Feature[];
  // 识别引擎升级（Task #54）：后端已暴露，前端需对齐契约
  coverage: CoverageResult;        // Coverage Check（漏检扫描）结果
  metrics: MetricsResult;          // Recall / Precision / Anchor Accuracy
  dropped: DroppedItem[];          // 被过滤对象（原因 + 分类）
  text_stats: TextStats;           // 文本通道（矢量 / OCR）统计
}

export interface AnalyzeResult {
  drawing: DrawingSummary;
  features: Feature[];
  dropped: DroppedItem[];
  stats: Record<string, any>;
  coverage: CoverageResult;
  metrics: MetricsResult;
  text_stats: TextStats;
}

// ============= 统一识别任务 RecognitionJob（V0.6）=============
export type JobStatus =
  | 'PENDING' | 'PROCESSING' | 'DECODING' | 'PREPROCESSING'
  | 'OCR' | 'PARSING' | 'COMPLETED' | 'FAILED' | 'TIMEOUT' | 'CANCELLED';

export interface JobError {
  code: string;
  message: string;
  detail?: string;
  retry_hints: string[];
}

export interface RecognitionJob {
  id: number;
  drawing_id: number | null;
  page_index: number;
  filename: string;
  ext: string;
  status: JobStatus;
  stage: string;
  stage_cn: string;
  progress: number;                  // 0..100
  message: string;
  file_analysis: Record<string, any>;
  preprocess: Record<string, any>;
  error_code: string | null;
  error: JobError | null;
  retry_hints: string[];
  feature_count: number;
  low_res: boolean;
  created_at: string | null;
  started_at: string | null;
  deadline_at: string | null;
  completed_at: string | null;
}

export interface UploadJobResponse {
  drawing_id: number;
  jobs: RecognitionJob[];
}


// ============= Coverage Check（漏检扫描）契约 =============
// 与后端 core_engine/parse/coverage.py CoverageItem.to_dict 对齐
export type CoverageSeverity = 'POSSIBLE_MISSED' | 'UNMATCHED';

export interface CoverageItem {
  text: string;
  bbox: number[];                  // [x0, y0, x1, y1] PDF 点
  page_no: number;                 // 0-based 页码
  x: number;                       // 文本中心 X（cx，PDF 点）
  y: number;                       // 文本中心 Y（cy，PDF 点）
  severity: CoverageSeverity;
  reason: string;
  category_guess: string;          // HOLE / RADIUS / ANGLE / THREAD / DIMENSION
  source: string;                  // vector / ocr
  confidence: number;
  has_geometry: boolean;
}

export interface CoverageResult {
  items: CoverageItem[];
  stats: {
    candidates: number;
    possible_missed: number;
    unmatched: number;
    covered_texts: number;
  };
}

// 后端 metrics / dropped / text_stats 结构宽松（以运行态为准）
export type MetricsResult = Record<string, any>;
export type DroppedItem = Record<string, any>;
export type TextStats = Record<string, any>;

// ============= 疑似漏检定位（Task #55）=============
// 点击徽章 / 画布标记时，在 UI store 中记录待定位项，驱动 Canvas 居中
export interface MissedItem {
  idx: number;                     // 在 coverage.items 中的序号
  x: number;                       // PDF 点（cx）
  y: number;                       // PDF 点（cy）
  page_no: number;
  text: string;
  category_guess: string;
  severity: CoverageSeverity;
  confidence: number;
  reason: string;
}

// 持久化气球布局（v0.21 / 画布消费）
export interface BalloonPos {
  feature_pk: number;
  feature_id: string;
  balloon_no: number;
  page_no: number;
  x: number;             // PDF 点，中心（兼容）
  y: number;
  center_x: number;      // 气球中心（PDF 点）
  center_y: number;
  anchor_x: number;      // 特征锚点（引线起止点）
  anchor_y: number;
  leader_points: number[][];  // 引线折点（可空）
  status: FeatureStatus;
  enabled: boolean;
}

export interface PageBalloons {
  drawing_id: number;
  page_index: number;
  page_width: number;    // PDF 点
  page_height: number;
  radius: number;
  count: number;
  balloons: BalloonPos[];
  /** 自检：圆面压住其他特征锚点（尺寸文字）的气球清单 */
  overlaps?: BalloonOverlap[];
}

export interface BalloonOverlap {
  feature_pk: number;
  balloon_no: number;
  overlap_with: string[];
}

// ---------------------------------------------------------------- 框选区域识别
/** 选区（页面坐标系：PDF=pt，栅格图=原始像素；与 Feature.x/y 同源） */
export interface RegionRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** 因与既有特征重复而被判定跳过的项 */
export interface RegionDuplicate {
  text: string;
  anchor: number[];
  reason: string;
  existing_feature_id: string;
  existing_balloon_no: number | null;
}

/** 框选区域识别结果（只追加新特征，不重建既有数据） */
export interface RegionRecognizeResult {
  drawing_id: number;
  page_index: number;
  region: RegionRect;
  recognized: number;   // 选区内识别到的特征数（含重复）
  added: number;        // 实际新增落库数
  skipped: number;      // 判重跳过数
  duplicates: RegionDuplicate[];
  created: Feature[];
  ocr?: Record<string, any>;
  text_stats?: Record<string, any>;
  stats?: Record<string, any>;
  duration_ms: number;
}

export const STATUS_CN: Record<FeatureStatus, string> = {
  AI_FOUND: '待确认',
  CONFIRMED: '已确认',
  MODIFIED: '已修改',
  REJECTED: '异常',
};

export const STATUS_HINT: Record<FeatureStatus, string> = {
  AI_FOUND: 'AI 自动识别，待人工确认',
  CONFIRMED: '已人工确认，纳入报告',
  MODIFIED: '已人工修改字段，待再次确认',
  REJECTED: '异常/已排除，不参与报告',
};

// 状态主色（画布/表格/徽章共用，避免多处重复）
export const STATUS_COLORS: Record<FeatureStatus, string> = {
  AI_FOUND: '#f9a825',   // 待确认 - 琥珀
  CONFIRMED: '#2e7d32',  // 已确认 - 绿
  MODIFIED: '#1565c0',   // 已修改 - 蓝
  REJECTED: '#9e9e9e',   // 异常 - 灰
};

// 状态流转：一键确认/修改/排除
export const STATUS_NEXT: Record<FeatureStatus, FeatureStatus> = {
  AI_FOUND: 'CONFIRMED',     // 待确认 → 已确认
  CONFIRMED: 'MODIFIED',     // 已确认 → 已修改（编辑态）
  MODIFIED: 'CONFIRMED',     // 已修改 → 重新确认
  REJECTED: 'AI_FOUND',      // 异常 → 重新纳入
};

// 这些字段被人工修改时，后端会自动把状态置为 MODIFIED
// （与 backend/services/feature_service.py: EDITABLE_FIELDS 对齐；v0.31.3 补 kind）
//
// ⚠️ 2026-10-16 核对：**本常量当前没有任何消费者**（全前端 grep 只有定义处），
//    真正生效的白名单是后端的 `EDITABLE_FIELDS`，另有一份**同名但不同语义**的
//    `EditKey`（'name'|'req'|'tol'，行内可直编的单元格）定义在 FeatureTable.tsx 内部。
//    别拿它当白名单用 —— 往里加字段不会放行任何东西，只会让人以为已经放行了。
//    （v0.58 补 `qty` 时就是先查到这点，才直接去改后端 schema 的。）
export const EDIT_KEYS = [
  'characteristic', 'nominal', 'upper_dev', 'lower_dev',
  'tolerance_text', 'method', 'precision', 'type', 'unit',
  'upper_tol', 'lower_tol', 'note', 'display', 'display_text', 'kind',
] as const;
export type EditKey = typeof EDIT_KEYS[number];

// ============= 实测数据 / 过程能力（Form4 / CPK）=============
// 与后端 MeasurementUpsert + capability_service 响应对齐
export type CapabilityStatus =
  'capable' | 'not_capable' | 'insufficient_data' | 'no_limits';

export interface Measurement {
  feature_id: number;
  drawing_id: number;
  measured: number | null;
  samples: number[];
  inspector: string | null;
  measured_at: string | null;
}

/** 单特性过程能力（与后端 CharacteristicCapability 对齐）。feature_id 为字符串，对应 Feature.feature_id（如 "F001"）。 */
export interface CharacteristicCapability {
  feature_id: string;
  characteristic: string;
  unit?: string | null;
  nominal: number | null;
  usl: number | null;
  lsl: number | null;
  n: number;
  mean: number | null;
  sigma: number | null;
  cp: number | null;
  cpk: number | null;
  pp: number | null;
  ppk: number | null;
  status: CapabilityStatus;
  note: string;
}

export interface CapabilityResult {
  drawing_id: number;
  threshold: number;
  items: CharacteristicCapability[];
  summary: {
    total: number;
    capable: number;
    not_capable: number;
    insufficient_data: number;
    no_limits: number;
    evaluated: number;
    avg_cpk: number | null;
  };
}

/** 能力状态配色（capable=绿 / not_capable=红 / 其它=灰），供表格与汇总复用 */
export const CAP_COLORS: Record<CapabilityStatus, string> = {
  capable: '#2e7d32',
  not_capable: '#c62828',
  insufficient_data: '#9e9e9e',
  no_limits: '#9e9e9e',
};

export const CAP_CN: Record<CapabilityStatus, string> = {
  capable: '能力充足',
  not_capable: '能力不足',
  insufficient_data: '样本不足',
  no_limits: '无规格限',
};

// ================================================================ 文件夹（v0.32；v0.43 起图纸与零件共用）
/** 一个文件夹节点（自引用递归）。
 *
 *  ★ v0.43：`folders` 是**图纸与零件共用**的一棵树（见 `PartMasterRow.folder_id`），
 *    所以每个节点同时带两类对象的计数。不另建第二棵树 —— 本项目头号坑就是
 *    「同一语义几份实现」。
 */
export interface FolderNode {
  id: number;
  name: string;
  parent_id: number | null;
  sort_order: number;
  /** 直属图纸数（前端徽标显示这个） */
  drawing_count: number;
  /** 含子孙的图纸总数（折叠时用来提示「这个夹子里一共几张」） */
  total_drawing_count: number;
  /** 直属零件数（v0.43；零件主数据页的节点徽标） */
  part_count: number;
  /** 含子孙的零件数（v0.43） */
  total_part_count: number;
  children: FolderNode[];
}

/** GET /api/folders —— 整棵树 + 全部图纸（每条带 folder_id）。 */
export interface FolderTree {
  folders: FolderNode[];
  /** 全部图纸。展开文件夹时前端**本地按 folder_id 过滤**，不再逐个打接口。 */
  drawings: DrawingSummary[];
  /** `drawings` 中 folder_id 为空的那部分（保留旧形状，兼容既有消费方）。 */
  root_drawings: DrawingSummary[];
  folder_count: number;
  unfiled_count: number;
  /**
   * 未分类零件数（v0.43）。零件主数据页左侧「未分类」节点的徽标。
   * ⚠️ 判据与 `GET /api/part-master?unfiled=1` **同源**（后端一份
   *    `repo.part_unfiled_cond`）—— 否则会出现「树说 3 个、点进去 2 个」。
   */
  unfiled_part_count: number;
}

/** DELETE /api/folders/{id} 的返回：文件夹删了，里面的图纸退回根目录、零件退回未分类。 */
export interface FolderDeleteResult {
  deleted_folders: number;
  drawings_to_root: number;
  /** 被退回「未分类」的零件数（v0.43）—— 用它如实提示，不能只说「已删除」。 */
  parts_to_unfiled: number;
}

// ================================================================ v0.32 测量报告
/** 当前生效的《测量报告》模板。`ok=false` 时 UI 必须显示 problems。 */
export interface MeasureReportTemplateInfo {
  exists: boolean;
  ok: boolean;
  problems: string[];
  source: 'builtin' | 'uploaded' | '';
  filename: string;
  path: string;
  size_bytes: number;
  updated_at: string | null;
  sheets: string[];
}

/** 表头 12 字段，全部可空 —— 抽不到就留空，绝不编造。 */
export interface MeasureReportHeader {
  part_name?: string | null;
  part_no?: string | null;
  version?: string | null;
  material?: string | null;
  drawing_no?: string | null;
  inspector?: string | null;
  lot_no?: string | null;
  prod_qty?: string | null;
  ship_lot?: string | null;
  inspection_date?: string | null;
  measure_qty?: string | null;
  ship_qty?: string | null;
}

/** 导出前预检：会导出什么 / 要不要插行 / 表头抽到了什么 / 有什么风险。 */
export interface MeasureReportPreflight {
  exists: boolean;
  drawing_id: number | null;
  filename: string;
  template: MeasureReportTemplateInfo;
  template_capacity: number;
  insert_rows: number;
  unhide_rows: number;
  feature_total: number;
  will_export: number;
  /** v0.54：预检统计是否按「测量报告编辑页」保存的草稿算（弹窗据此展示出处） */
  from_draft: boolean;
  draft_count: number;
  reference_count: number;
  /** v0.33：形位公差内容写进 M 备注列的条数 */
  gdt_remark: number;
  no_tolerance: number;
  needs_manual: number;
  excluded: number;
  methods_unknown: Array<[number, string]>;
  header: MeasureReportHeader;
  /** 每个表头字段的出处：auto / filename / auto(同零件编号) / filename(同零件编号) / empty */
  header_source: Record<string, string>;
  /** 文件名里的图号候选（0/1/多）。多候选时 header.part_no 故意留空，弹窗给点选 */
  filename_candidates: string[];
  header_spans: number;
  header_note: string;
  warnings: string[];
  balloon_png: boolean;
}

// v0.31（2026-10-03，用户要求）：以下类型已随功能整体下线而移除 ——
//   · 《尺寸检验指导书》SIP：SipPreflight / SipTemplateInfo / SipTemplateList /
//     SipTemplateSheet / SipTemplateWorkbook / SipCell / SipGrid / SipWriteResult / SipBackup
//   · AI 标题栏抽取：AiStatus / AiAuditStats / AiTitleblockResult
// 对应前端入口、面板组件、`/api/sip/*` 与 `/api/ai/*` 后端接口一并删除。

// ================================================================ 零件主数据（v0.38）
// 需求（2026-10-09）：单独一个栏目，16 项 —— 能识别的识别、识别不了的自己填。
//
// ⚠️⚠️ **字段清单的单一出处是后端** `backend/report/part_master.py`
//      （`FIELD_GROUPS` / `FIELD_CN` / `ALL_FIELDS`）。下面 `PM_FIELD_GROUPS` /
//      `PM_FIELD_CN` 是**镜像**，由 `backend/tests/test_part_master_v038.py`
//      的守护用例逐字比对 —— 后端改字段而忘了同步这里，用例会红。
//      （不改成"接口下发字段表"是因为这 16 项已由用户冻结，加一个只为取中文名的
//        接口，代价大于收益；用护栏守住更划算。）
export interface PartMasterRow {
  id: number;
  // ---- 项目层
  project_type: string | null;
  customer: string | null;
  internal_no: string | null;
  project_name: string | null;
  // ---- 模具层
  mold_no: string | null;
  mold_type: string | null;
  cavity_count: string | null;
  press_tonnage: string | null;
  // ---- 零件层
  part_no: string | null;
  part_name: string | null;
  drawing_rev: string | null;
  material_grade: string | null;
  material_spec: string | null;
  material_supplier: string | null;
  /** ⚠️ 列名是 surface_treatment，中文名**全链路统一「表面处理」**（不叫「表处理」） */
  surface_treatment: string | null;
  process_flow: string | null;
  // ---- 备用列
  tech_requirements: string | null;
  note: string | null;
  /** 逐字段来源：auto（标题栏锚定）/ filename（取自文件名）/ manual（人工）/ empty */
  source: Record<string, string>;
  /**
   * 该行**已归户**的图纸张数（服务端算出来的只读字段，v0.39）。
   * 0 ⇒ 列表里显示灰标「无图纸」—— 用户要求「删图纸后不要删行，但要标出来」。
   * ⚠️ 只读：不能出现在请求体里（见下面 `PartMasterUpsert` 的 Omit）。
   */
  drawing_count: number;
  /**
   * 这一行下面挂了几个子零件（服务端算出来的只读字段，v0.41 BOM）。
   * `> 0` ⇒ 界面上这一行的角色是**总成**（可以展开看子件）。
   * ⚠️ 「总成」不是一个存下来的类型 —— 由关系反推，避免「标记说是总成、
   *    实际一个子件都没有」这种第二份真相。
   */
  child_count: number;
  /**
   * 这一行装在几个总成下（只读，v0.41 BOM）。
   * `> 1` ⇒ 它是**共用件**（同一零件装在多个总成下）—— 这正是当初选边表而不是
   * `parent_id` 的理由，界面靠这个数把它标出来。
   * ⚠️ 两个计数都是只读：不能出现在请求体里（见下面 `PartMasterUpsert` 的 Omit）。
   */
  parent_count: number;
  /**
   * 该行所属文件夹（v0.43）；`null` = **未分类**。
   * ⚠️ 只读：**不能**出现在请求体里（见下面 `PartMasterUpsert` 的 Omit）——
   *    后端 `PUT /part-master/{id}` 是**全量**语义（未传的键 = 置空），
   *    混进去的话任何一次漏提交都会把分类**静默清空**。
   *    改归属只能走 `setPartFolder` / `bulkSetPartFolder`。
   */
  folder_id: number | null;
  /**
   * 文件夹名（v0.43，服务端按 `folder_id` 反查出来的只读显示名）。
   * 名字实时取自 folders 表 ⇒ 改完文件夹名，列表里这一列立刻是新的。
   * 后端**悬空引用**（指向已删夹子）时给 `null` ⇒ 界面按「未分类」显示。
   */
  folder_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** 新建 / 更新请求体（16 项 + 备用列；未提供的键 = 置空）。
 *
 *  ⚠️ `drawing_count` / `child_count` / `parent_count` 三个都要 Omit —— 它们是
 *     服务端算出来的，带上会误导成"客户端能指定这行挂几张图、有几个子件"
 *     （后端 schema 也不收）。
 *  ⚠️ `folder_id` / `folder_name` 同样要 Omit，而且这条**更要紧**：后端 PUT 是
 *     全量语义，`folder_id` 一旦进了请求体，前端任何一次漏提交就会把分类
 *     **静默清空**且不报错。改归属走专用接口。
 */
export type PartMasterUpsert = Omit<
  PartMasterRow, 'id' | 'source' | 'drawing_count'
                | 'child_count' | 'parent_count'
                | 'folder_id' | 'folder_name'
                | 'created_at' | 'updated_at'>;

/** 打开图纸时的自动带出结果。**只认唯一命中**：多命中时 hit=null 而 candidates 有值。 */
export interface PartMasterMatch {
  hit: PartMasterRow | null;
  candidates: PartMasterRow[];
  keys: Array<[string, string]>;
  extracted: Record<string, string | null>;
  extract_source: Record<string, string>;
  filename_candidates: string[];
  note: string;
}

export interface PartMasterDeleteResult {
  deleted: number;
  /**
   * 被解除归户的图纸数。
   * ⚠️ **v0.57 起恒为 0** —— 删除零件不再「只解绑」：名下图纸一起进回收站
   *    （见 `deleted_drawings`）。保留字段只是为了不把老数据的键读空，
   *    界面上不要再为它写分支。
   */
  unbound_drawings: number;
  /**
   * v0.57：**一并进回收站**的图纸张数（含其特征 / 识别运行 / SIP 草稿）。
   * 用它在提示里如实说明「N 张图纸一并删除，可在『回收站』整批恢复」——
   * 数字必须来自后端返回值，不许写死（删 0 张和删 3 张是两种事）。
   */
  deleted_drawings: number;
  /**
   * 被一并清掉的 BOM 关系条数（作为总成的 + 作为子件的），v0.41。
   * 用它如实提示「顺带解除了 N 条 BOM 关系」—— **对端零件行保留**。
   */
  deleted_bom_edges: number;
  missing: number;
}

// ============= 文件夹分类（v0.43）=============
/**
 * 移动图纸的结果（v0.57）。
 *
 * ★ 为什么不复用 `DrawingSummary`：v0.57 起**图纸的文件夹归属跟随它的归属零件**
 *   —— 把一张已归户的图纸拖进某个夹子，事实上是「零件 + 该零件名下全部图纸」
 *   一起搬。只回一张图纸的话，提示只能写死「已移动」，而用户看不到**零件主数据**
 *   那一屏里也动了东西。
 *   `moved_parts === 0 && moved_drawings === 1` ⇒ 这是**未归户**的图纸，只动了它自己。
 */
export interface MoveDrawingResult {
  drawing: DrawingSummary;
  moved_parts: number;
  moved_drawings: number;
}

/**
 * 批量改归属的结果。
 *
 * ⚠️ `moved` 是**库里实际改动的行数**，不是 `ids.length`：ids 里混入已删除的 id
 *    时两者不一致。提示要说实话（「已移动 7 个，1 个已不存在」），否则用户会以为
 *    某个零件明明没出现在新文件夹里是界面坏了。
 * `folder_name === null` ⇒ 目标就是「未分类」。
 */
export interface PartMasterBulkFolderResult {
  moved: number;
  missing: number;
  folder_name: string | null;
  /**
   * v0.57：这批零件名下**跟着一起搬**的图纸张数。
   *
   * ★ 为什么不能拿 `moved` 去推：一个零件名下可能挂着 0 张也可能挂着 3 张图纸。
   *   而一次「移到某夹子」现在会连带挪动**另一个页签**（图纸工作台）里的对象 ——
   *   只报零件数，用户会以为图纸还留在原地。
   */
  moved_drawings: number;
}

/**
 * 零件列表的**归属范围**（v0.43）。三个互斥取值：
 *   `{kind:'all'}`                 全部零件
 *   `{kind:'folder', id}`          某个文件夹里的
 *   `{kind:'unfiled'}`             未分类
 *
 * ★ 用一个判别联合而不是 `folderId?: number | null` 两个散参数：分类树上
 *   「未分类」**也是**一个可点的节点，用散参数表达时 `null` 既是"不筛选"又是
 *   "未分类"，正是 `folder_id=0` 那类兜底 bug 的温床。
 */
export type PartMasterScope =
  | { kind: 'all' }
  /** @deprecated v0.85：台账废弃后列表已不按文件夹筛（左栏换成客户维度） */
  | { kind: 'folder'; id: number }
  /** @deprecated 同上 */
  | { kind: 'unfiled' }
  /** v0.85（沈哥 2026-10-22）：按 **BOM 客户字段** 筛选。
   *  `name = '__none__'` ⇒ 「其他」（客户字段为空的那些）。 */
  | { kind: 'customer'; name: string };

// ============= BOM 父子关系（v0.41）=============
/**
 * 一条 BOM 关系（「总成 → 子零件」）。
 *
 * 需求（2026-10-11 用户原话）：
 *     「BOM那里需要优化多级，总成、总成下面还有子零件」
 *
 * ⚠️ `id` 是**边**的 id，**不是零件 id** —— 改用量/位号、解除关系都用它。
 *    `parent_pm_id` / `child_pm_id` 才是零件（对应 `PartMasterRow.id`）。
 *    两者都是小整数，传错一个会去改到另一个零件的关系，界面上一个字母之差
 *    很难看出来 —— 所以字段名刻意不叫 `parent_id` / `child_id`。
 *
 * `other_id` / `part_no` / `part_name` / `internal_no` 是**对面那一行**的摘要：
 *   · 在「子零件」栏（`listPartChildren`）里，对面是子件；
 *   · 在「所属总成」栏（`listPartParents`）里，对面是总成。
 * 由接口决定，前端不必自己判 —— 但**显示时要想清楚这一段是哪个语义**。
 */
export interface BomEdge {
  id: number;
  parent_pm_id: number;
  child_pm_id: number;
  /** 用量。**字符串**：现场有「1 SET」这类写法，按数字收会吃掉它们 */
  qty: string | null;
  /** 位号 */
  position: string | null;
  note: string | null;
  created_at: string | null;
  other_id: number;
  part_no: string | null;
  part_name: string | null;
  internal_no: string | null;
  /** v0.48：对面那一行的**完整数据**。
   *
   *  只有 `listPartChildren(pmId, true)`（对应后端
   *  `GET /{pm_id}/children?with_row=true`）时才由后端填充；
   *  其余场景（编辑面板的「子零件」栏、`/parents`）是 `null`。
   *
   *  ⚠️ 列表页展开区**必须**用这个而不是那三个摘要字段 —— 展开区按
   *     「表头勾选了哪几列就显示哪几列」渲染，客户 / 材料牌号 / 图纸数
   *     这些都不在摘要里。
   *  ⚠️ 多级 BOM 靠 `row.child_count` 判断「这个子件自己还有没有下级」——
   *     它是**能继续下钻的唯一依据**，别当成可选项。
   */
  row?: PartMasterRow | null;
}

/** 加一个子零件。
 *
 *  ⚠️ 两个零件行都必须**已经存在** —— 本接口只建立关系，**不建零件**
 *     （与「关联图纸不能上传图纸」同一条原则：一个接口只做一件事）。
 */
export interface BomEdgeCreate {
  child_id: number;
  qty?: string | null;
  position?: string | null;
  note?: string | null;
}

/**
 * 改用量 / 位号 / 备注。
 *
 * ⚠️ **全量**语义：没传的字段 = 置空（只更新传了的键，会让"漏提交一个字段"
 *    静默保留旧值）。
 * ⚠️ **改不了父子** —— 换父子 = 删一条、建一条（让 PATCH 也能改父子，就得在这里
 *    再跑一遍环路检测，而且出错时界面说不清是改关系失败还是改属性失败）。
 */
export interface BomEdgePatch {
  qty?: string | null;
  position?: string | null;
  note?: string | null;
}

/** 解除关系的结果。两个零件行**都保留** —— 删的是关系，不是实物。 */
export interface BomEdgeDeleteResult {
  deleted: number;
  parent_pm_id: number;
  child_pm_id: number;
}

// v0.39：`PartMasterBulkCustomerResult` 已随「批量改客户」一并删除
// （用户 2026-10-10：「批量改客户那个功能也删除」）。改客户改为逐行编辑。

// ---------------------------------------------------------------- 识别文字（v0.42）
/** 识别时读到的一段文字原文（**一字不改**，OCR 读错也照原样留）。 */
export interface RecognizedTextRow {
  id: number;
  page_no: number;
  /** 原文。⚠️ **不做任何清洗** —— 洗过的文本会掩盖识别错误，失去核对价值。 */
  text: string;
  /** 坐标**跟随页面坐标系**：`vector` 是 PDF 点(pt)，`ocr` 是原始像素(px)。
   *  ⚠️ 两者不能画进同一个坐标系；`null` = 该来源没给框。
   *  ⚠️ 0 是合法坐标 ⇒ 渲染时**不要**用 `x0 || '—'` 这类兜底。 */
  x0: number | null;
  y0: number | null;
  x1: number | null;
  y1: number | null;
  source: 'vector' | 'ocr' | null;
  confidence: number | null;
}

/** 某张图的识别文字列表 + 「到底有没有记录过」的标记。 */
export interface RecognizedTextList {
  drawing_id: number;
  page_no: number | null;
  /** ★ 这四项是**筛选前**的计数，但**跟随 `page_no`**（页是范围不是筛选）。 */
  total: number;
  vector: number;
  ocr: number;
  /** ★ `false` = 这张图识别于 v0.42 之前，当时**不记录**文字。
   *  此时 `total === 0` 不代表"没读到" —— 重新识别一次就能看到。
   *  没有这个标记，界面只能把老数据一律显示成"没识别到文字"。 */
  recorded: boolean;
  recorded_rows: number;
  /** 本次实际返回的条数（受 `q` / `source` / `limit` 影响）。 */
  shown: number;
  items: RecognizedTextRow[];
}

// ---- 镜像常量（单一出处见文件顶部说明）
export const PM_FIELD_GROUPS: Array<[string, Array<keyof PartMasterRow>]> = [
  ['项目', ['project_type', 'customer', 'internal_no', 'project_name']],
  ['模具', ['mold_no', 'mold_type', 'cavity_count', 'press_tonnage']],
  ['零件', ['part_no', 'part_name', 'drawing_rev', 'material_grade',
            'material_spec', 'material_supplier', 'surface_treatment',
            'process_flow']],
];

export const PM_FIELD_CN: Record<string, string> = {
  project_type: '项目类型', customer: '客户', internal_no: '内部编号',
  project_name: '项目号', mold_no: '模具编号', mold_type: '模具类型',
  cavity_count: '模穴', press_tonnage: '冲床吨位', part_no: '零件编号',
  part_name: '零件名称', drawing_rev: '图纸版本', material_grade: '材料牌号',
  material_spec: '材料规格', material_supplier: '材料厂家',
  surface_treatment: '表面处理', process_flow: '工艺流程',
};

/** 16 项（展开顺序 = 编辑面板 / 导出列顺序）。 */
export const PM_ALL_FIELDS: string[] =
  PM_FIELD_GROUPS.flatMap(([, fs]) => fs as string[]);

/** 唯一键四列（界面上要标出来，因为撞键的提示就是围绕这四列说的）。 */
export const PM_BIZ_FIELDS = ['customer', 'internal_no', 'part_no', 'drawing_rev'];

/** 来源 → 中文徽章文案。`auto` 才配叫「识别」，`filename` 只是提示。 */
export const PM_SOURCE_CN: Record<string, string> = {
  auto: '识别', filename: '文件名', manual: '手填', empty: '未填',
};

// ================================================== SIP 作业指导书（v0.50）
// 需求（2026-10-14 用户原话）：
//   「不同的制程SIP点进去单独出现编辑页面」
//   「零件主数据里面手动输入的信息要跟后面 SIP 联动」
//   入口：「3、两个都要」= 零件主数据行 + 图纸行都放按钮
//
// ★ 与后端的边界（照抄 `services/sip_draft_service.py` 的三条纪律，别在界面
//   上把它们破掉）：
//   1. 表头**只读** —— 取值唯一出处是零件主数据，SIP 页不给填。
//   2. `is_dim` **由服务端派生**（`nominal is not None`），界面不传、也不许改。
//   3. 解析不出来的公差**不兜底** —— 后端会 400 并指到第几行，界面照原样显示。

export interface SipHeaderField {
  key: string;
  label: string;
  value: string;
  /** 这一份文档的表头**真的会用到**它吗（由适配器现算）。没用到的灰掉。 */
  used: boolean;
}

export interface SipItem {
  // ---- 文本列
  feature_id: string;
  kind: string;
  type: string;
  cc_sc: string;
  proj: string;
  method: string;
  /** v0.53：检验频率（全检 / 抽检 1/5 / 首件 …）—— 草稿自由文本，默认空。 */
  freq: string;
  spec: string;
  warn: string;
  // ---- 数字列（`null` = 空，不是 0）
  nominal: number | null;
  up: number | null;
  low: number | null;
  // ---- 整数列
  no: number | null;
  view: number | null;
  /** ★ 只读：服务端按 `nominal != null` 派生，界面**不许改**。 */
  is_dim: boolean;
}

/** 左栏的一项 = 一份文档（v0.55 起同一工序可以有多份）。
 *
 *  ★ `doc_kind + seq` 才是这一份的**唯一标识** —— 只按 `doc_kind` 索引会在
 *    多份上线后指向"这道工序的某一份"，而具体哪一份说不清。
 */
export interface SipDocStatus {
  doc_kind: string;
  /** 第几份（0 = 第一份）。老接口不带这个参数时后端按 0 处理。 */
  seq: number;
  /** 工序名（「制程」「成品」…）——**不带**份号，用于「-02 制程」那种前缀。 */
  label: string;
  /** 这一份在界面上叫什么：用户起过名就是那个名，否则第一份=工序名、
   *  第 N 份=「制程 N」。**唯一出处是后端**（`sip_draft_service.display_name`）。 */
  name: string;
  /** 用户起的名字（空串 = 没起名，界面用默认名显示）。 */
  title: string;
  /** `true` = 第六份文档「测量报告」（固定一份，不能复制/新建）。 */
  is_mr: boolean;
  has_draft: boolean;
  /** `true` = 存过明细（哪怕存的是空表 `[]`）；`false` = 草稿行在、但明细列是 NULL
   *  （只改过名）。
   *  ⚠️ **不要**拿它判「这份有没有内容」：空表也是 `true` ⇒ 显示成「已存 0 行」。
   *     界面要判「有没有明细」一律看 `item_count === 0`。 */
  edited: boolean;
  item_count: number;
  updated_at: string | null;
  updated_by: string;
}

export interface SipStats {
  n: number;
  dims: number;
  specs: number;
  cc_sc: number;
  by_kind: Record<string, number>;
}

export interface SipPageData {
  drawing_id: number;
  doc_kind: string;
  /** v0.55：这一份的份号（0 = 第一份）。 */
  seq: number;
  /** v0.83：这道 SIP 属于哪道工序（`0` = 常规五份 / 测量报告；非 0 = `route_ops.id`）。 */
  route_op_id?: number;
  /** 这一份的显示名（同 `docs[].name`）。 */
  name: string;
  /** 用户起的名字（空串 = 没起名）。 */
  title: string;
  /** 这道工序现有几份（至少 1；测量报告恒 1）。 */
  doc_count: number;
  label: string;
  filename: string;
  part_master_id: number | null;
  part_match: string;
  header: SipHeaderField[];
  /** 表头全空 = 这张图**未归户**；界面要给「先去归户」的提示，不给可填的框。 */
  header_empty: boolean;
  /** `draft` = 用户上次保存的草稿（权威）；`default` = 按工序规则现算的初值。 */
  source: "draft" | "default";
  items: SipItem[];
  /** 图纸特征全集 —— 界面「从特征添加」的候选。 */
  available: SipItem[];
  remark: string;
  updated_at: string | null;
  updated_by: string;
  warnings: string[];
  stats: SipStats;
  docs: SipDocStatus[];
  item_fields: { text: string[]; number: string[]; integer: string[] };
}

export interface SipDraftListResult { drawing_id: number; docs: SipDocStatus[]; }
export interface SipDraftSavedResult {
  drawing_id: number; doc_kind: string; seq: number; name: string;
  saved: number; stats: SipStats;
}
export interface SipDraftDeleteResult {
  drawing_id: number; doc_kind: string; seq: number; deleted: number;
}

/** v0.55：「复制一份 / 新建空份」的返回 —— 新份落在末尾，界面直接切过去编辑。 */
export interface SipDraftActionResult {
  drawing_id: number; doc_kind: string; seq: number;
  /** 新份的显示名（默认名，用户想改再右键重命名）。 */
  name: string;
  saved: number;
  doc_count: number;
  /** 仅「复制一份」有：从哪一份复制来的。 */
  from_seq?: number;
  /** 仅「复制一份」有：新份的内容来自源份的草稿还是默认分配（提示语用）。 */
  source_of_new?: "draft" | "default";
}

/** v0.55：改名结果。`title=""` 表示恢复默认名（`name` 就是默认名）。 */
export interface SipRenameResult {
  drawing_id: number; doc_kind: string; seq: number;
  title: string; name: string;
}

/**
 * SIP 页的入口来源 —— **三条路三种形态**。
 *
 * · `drawing`：从图纸行点进来，图纸 id 现成的，直接用。
 * · `part`   ：从零件主数据行点进来 —— 台账行**不绑定图纸**（0 张合法、
 *              也常有 2 张以上），所以这里只给零件 id，由 SIP 页自己去
 *              「这个零件名下的图纸」里选一张。
 * · `pick`   ：从**顶栏常驻 tab** 点进来（v0.52，用户 2026-10-15「作业指导书
 *              也要跟主数据一样在顶部显示」）—— 没有目标，由 SIP 页给一张
 *              **全库图纸选择列表**；选完之后本页行为与 `drawing` 完全一致。
 * ⚠️ 前两种不让调用方先查一次再传 drawingId：那样「0 张 / 多张」的分支会写在
 *    两个入口里（图纸行那份根本用不上），迟早只改一处。
 */
export type SipEntry =
  | { kind: "drawing"; id: number; label?: string;
      /** v0.90：从物料树直接点开某一份 SIP 时携带，SIP 页据此预选。 */
      docKind?: string; seq?: number; routeOpId?: number }
  | { kind: "part"; id: number; label?: string }
  | { kind: "pick" };

// ============= SIP 物料树（v0.90，沈哥重设计）=============
/**
 * 物料树里**可点开的叶子** = 一份 SIP（不是图纸）。
 * 同产品多份 SIP 共用一份气泡图，所以树只到 SIP 这一层。
 */
export interface SipTreeLeaf {
  drawing_id: number;
  /** 同一物料有多张图时才带（用于区分）；否则前端不显示。 */
  drawing_label?: string;
  /** "01".."05" | "MR"。 */
  doc_kind: string;
  /** 第几份（0 = 第一份）。 */
  seq: number;
  /** 0 = 常规五份 / 测量报告；非 0 = 工序 SIP（route_ops.id）。 */
  route_op_id: number;
  /** 工序中文名 / 工序名 / 「测量报告」。 */
  label: string;
  /** 显示名（含份号或用户自定义名）。 */
  name: string;
  is_route_op: boolean;
  has_draft: boolean;
  item_count: number;
  /** 搜索模式：客户 · 产品。 */
  breadcrumb?: string;
  material_code?: string;
}

/** 物料树节点（成品 / 半成品）。`children` 是它下面的半成品（递归）。 */
export interface SipTreeNode {
  code: string;
  name: string;
  type: "finished" | "semi";
  drawing_count: number;
  sips: SipTreeLeaf[];
  children: Record<string, SipTreeNode>;
}

/** 客户层。 */
export interface SipTreeCustomer {
  name: string;
  nodes: Record<string, SipTreeNode>;
}

/** `GET /api/sip/tree` 的响应。 */
export interface SipTreeResult {
  mode: "tree" | "search";
  customers?: SipTreeCustomer[];
  matches?: SipTreeLeaf[];
}

// ============= 回收站（v0.56）=============
/**
 * 回收站条目的实体类型 —— **字面量与后端 `db/models.py` 的
 * `TRASH_DRAWING/TRASH_PART/TRASH_BOM_EDGE/TRASH_FOLDER` 逐字一致**。
 * ⚠️ 别在这里另写一套中文映射：中文名由后端 `trash_service.ENTITY_LABELS`
 *    随 `entity_type_cn` 一起下发（唯一出处）。
 */
export type TrashEntityType = "drawing" | "part" | "bom_edge" | "folder";

/** `GET /api/trash` 的一条（**不含快照本体** —— 快照可能几百 KB）。 */
export interface TrashEntry {
  id: number;
  entity_type: TrashEntityType | string;
  /** 中文类型名（「图纸 / 零件行 / BOM 关系 / 文件夹」），后端给。 */
  entity_type_cn: string;
  /** 删除时的主键，**仅供追溯**；恢复走快照，不依赖它。 */
  entity_id: number;
  label: string;
  detail: string;
  /** 这次动作一共涉及多少行（图纸=特征数等），后端算好。 */
  item_count: number;
  /** 同一次用户动作产生的多条共用一个 batch_id（可整批恢复）。 */
  batch_id: string;
  deleted_at: string | null;
  deleted_by: string;
  restored_at: string | null;
  restored_by: string | null;
  restored: boolean;
}

export interface TrashList {
  items: TrashEntry[];
  /** 当前**未恢复**条数（角标用；与 `items.length` 不是一回事 —— 列表有分页）。 */
  count: number;
}

/** `GET /api/trash/{id}`：多一份「会恢复哪些表、各多少行」。 */
export interface TrashDetail extends TrashEntry {
  tables: Array<{ table: string; rows: number }>;
  refs: Record<string, any>;
}

/** 恢复时对「关系」的处理结果（文件夹归属 / 零件归属）。 */
export interface TrashRestoreRefs {
  folder_drawings: number;
  folder_parts: number;
  part_drawings: number;
  /** 「不覆盖」而被跳过的条数（图纸已重新归到别的零件名下等）。 */
  skipped: number;
}

export interface TrashRestoreResult {
  id: number;
  entity_type: string;
  entity_type_cn: string;
  label: string;
  restored_rows: number;
  tables: Record<string, number>;
  refs: TrashRestoreRefs;
  /** ⚠️ 必须**原样展示**：少了什么要说清楚，不能只显示「已恢复」。 */
  warnings: string[];
}

export interface TrashBatchRestoreResult {
  restored: TrashRestoreResult[];
  failed: Array<{ id?: number; label?: string; error?: string }>;
}

export interface TrashPurgeResult {
  purged: number;
  label: string;
}

// ================================================================ 选项字典（v0.61）
/**
 * 一条后台可维护的选项。
 *
 * ★ 为什么要后台化：检测工具（量具）原先写死在 `lib/annot.ts` 的 `METHOD_OPTS`
 *   里，加一项就得改代码、重建前端、重启服务。而它同时是气泡图页下拉、
 *   指导书页下拉、测量报告 F 列三个地方的**共同输入** —— 代码里的常量做不到
 *   "用户自己加，三处一起变"。
 */
export interface OptionItem {
  id: number;
  kind: string;
  /** 服务端给的中文类名（前端**不再**自己维护一份 kind→中文） */
  kind_cn: string;
  value: string;
  /** 仅检测工具：写进测量报告 F 列的名字。空 = 原样写 `value`。 */
  report_name: string | null;
  /** 仅检测工具：测量值小数位（0=✓/✗ 定性）。 */
  precision: number | null;
  sort_order: number;
  enabled: boolean;
  /** 出厂预置项（用户加的为 false，「恢复出厂」只碰 true 的） */
  builtin: boolean;
  note: string;
}

/**
 * `GET /api/settings/options` 的响应。
 *
 * `items` = 全量（含停用）→ 设置页；
 * `values` = 启用中的纯值（库空时回落到出厂预置）→ 各处下拉。
 * 分成两份是因为「停用不出现、空则兜底」这条规则只该有**一处**实现。
 */
export interface OptionsResponse {
  kind_cn: Record<string, string>;
  items: Record<string, OptionItem[]>;
  values: Record<string, string[]>;
}

export interface OptionDeleteResult {
  ok: boolean;
  id: number;
  value: string;
  /** 还有多少行业务数据在用这个值（`null` = 无法统计） */
  used_by: number | null;
}

export interface OptionResetResult {
  ok: boolean;
  kind: string;
  added: number;
  fixed: number;
}
